// src/pages/cart/CartPage.tsx
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../../contexts/CartProvider";

/* ======================
   HELPERS
====================== */
const RAW_API_URL =
  process.env.REACT_APP_API_URL || process.env.NEXT_PUBLIC_API_URL || "";
const API_URL = RAW_API_URL.replace(/\/+$/, "");

function assetUrl(url?: string | null) {
  if (!url) return "/images/placeholder.jpg";
  const u = String(url).trim();

  if (u === "/images" || u === "/images/") return "/images/placeholder.jpg";
  if (/^https?:\/\//i.test(u)) return u;

  if (u.startsWith("/")) return API_URL ? `${API_URL}${u}` : u;
  return API_URL ? `${API_URL}/${u}` : u;
}

type SafeImgProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  src?: string;
};

function SafeImg({ src, ...props }: SafeImgProps) {
  const [failed, setFailed] = useState(false);

  React.useEffect(() => {
    setFailed(false);
  }, [src]);

  const finalSrc =
    failed ? "/images/placeholder.jpg" : src || "/images/placeholder.jpg";

  return (
    <img
      {...props}
      src={finalSrc}
      loading={props.loading ?? "lazy"}
      decoding={props.decoding ?? "async"}
      onError={() => setFailed(true)}
    />
  );
}

export default function CartPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { items, updateItem, removeItem } = useCart();

  const lang = i18n.language?.startsWith("en") ? "en" : "vi";
  const moneyLocale = lang === "en" ? "en-US" : "vi-VN";

  const formatMoney = (n: number) =>
    (Number.isFinite(n) ? n : 0).toLocaleString(moneyLocale, {
      maximumFractionDigits: 0,
    }) + "₫";

  const [busyKey, setBusyKey] = useState<string | null>(null);

  const total = useMemo(
    () =>
      items.reduce(
        (sum, i) =>
          sum + (Number(i.unitPrice) || 0) * (Number(i.quantity) || 0),
        0,
      ),
    [items],
  );

  const dec = async (cartLineId: number, qty: number) => {
    const key = String(cartLineId);
    if (busyKey) return;
    setBusyKey(key);
    try {
      const next = qty - 1;
      if (next <= 0) await removeItem(cartLineId);
      else await updateItem(cartLineId, next);
    } finally {
      setBusyKey(null);
    }
  };

  const inc = async (cartLineId: number, qty: number) => {
    const key = String(cartLineId);
    if (busyKey) return;
    setBusyKey(key);
    try {
      await updateItem(cartLineId, qty + 1);
    } finally {
      setBusyKey(null);
    }
  };

  if (!items.length) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 space-y-8">
        <p className="text-[11px] uppercase tracking-[0.45em] text-black/60">
          {t("cart.pageEmpty")}
        </p>

        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.45em] text-black/60 hover:text-black transition"
        >
          <span className="h-px w-10 bg-black/30" />
          {t("cart.pageContinueShopping")}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-24 py-14 space-y-12">
      {/* HEADER */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.45em] text-black/45">
            {t("cart.pageKicker")}
          </p>
          <h1 className="mt-3 text-[22px] uppercase tracking-[0.35em] text-black">
            {t("cart.pageTitle")}
          </h1>
          <div className="mt-6 h-px w-16 bg-black/15" />
        </div>

        <Link
          to="/collection"
          className="text-[10px] uppercase tracking-[0.45em] text-black/45 hover:text-black transition"
        >
          {t("cart.pageContinueShopping")}
        </Link>
      </div>

      {/* LIST */}
      <div className="divide-y divide-black/10 border-y border-black/10">
        {items.map((item) => {
          // ✅ item.variantId thực tế là cartId (id dòng giỏ)
          const cartLineId = Number(item.variantId) || 0;

          const name =
            item.productName?.trim() ||
            t("cart.pageItemFallback", { id: cartLineId });

          const img = assetUrl((item as any).imageUrl);

          const qty = Number(item.quantity) || 0;
          const unit = Number(item.unitPrice) || 0;
          const lineTotal = unit * qty;

          const rowBusy = busyKey === String(cartLineId);

          return (
            <div
              key={cartLineId}
              className="py-7 flex items-center justify-between gap-6"
            >
              {/* LEFT: IMAGE + INFO */}
              <div className="flex items-center gap-5 min-w-0">
                <div className="h-20 w-16 overflow-hidden rounded-xl bg-black/5 flex-shrink-0 border border-black/10">
                  <SafeImg src={img} alt={name} className="h-full w-full object-cover" />
                </div>

                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.35em] text-black truncate">
                    {name}
                  </p>

                  {/* ✅ show color/size */}
                  {(((item as any).color || (item as any).size) && (
                    <p className="mt-2 text-[10px] uppercase tracking-[0.35em] text-black/45">
                      {(item as any).color
                        ? t("cart.pageColor", { v: (item as any).color })
                        : ""}
                      {(item as any).color && (item as any).size ? " · " : ""}
                      {(item as any).size
                        ? t("cart.pageSize", { v: (item as any).size })
                        : ""}
                    </p>
                  )) as any}

                  {/* ✅ đúng nghĩa: cart line id */}
                  <p className="mt-2 text-[10px] uppercase tracking-[0.35em] text-black/35">
                    {t("cart.pageLineId", { id: cartLineId })}
                  </p>

                  <p className="mt-4 text-[10px] uppercase tracking-[0.35em] text-black/35">
                    {t("cart.pageTipRemove")}
                  </p>
                </div>
              </div>

              {/* RIGHT: QTY + PRICE */}
              <div className="flex items-center gap-8">
                {/* QTY */}
                <div className="flex items-center h-9 rounded-full border border-black/15 overflow-hidden">
                  <button
                    disabled={rowBusy}
                    onClick={() => dec(cartLineId, qty)}
                    className="w-10 h-9 text-[12px] hover:bg-black/5 active:scale-95 transition disabled:opacity-40"
                    aria-label={t("cart.pageAria.decreaseQty")}
                  >
                    −
                  </button>

                  <div className="w-10 text-center text-[12px] text-black">{qty}</div>

                  <button
                    disabled={rowBusy}
                    onClick={() => inc(cartLineId, qty)}
                    className="w-10 h-9 text-[12px] hover:bg-black/5 active:scale-95 transition disabled:opacity-40"
                    aria-label={t("cart.pageAria.increaseQty")}
                  >
                    +
                  </button>
                </div>

                {/* LINE TOTAL */}
                <div className="w-28 text-right">
                  <p className="text-[10px] uppercase tracking-[0.35em] text-black/45">
                    {t("cart.pageSubtotal")}
                  </p>
                  <p className="mt-2 text-[13px] tracking-wide text-black">
                    {formatMoney(lineTotal)}
                  </p>

                  <p className="mt-2 text-[10px] uppercase tracking-[0.35em] text-black/35">
                    {t("cart.pageEach", { price: formatMoney(unit) })}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* TOTAL */}
      <div className="flex items-center justify-between border-b border-black/10 pb-8">
        <p className="text-[11px] uppercase tracking-[0.45em] text-black/60">
          {t("cart.pageTotal")}
        </p>
        <p className="text-[18px] tracking-wide text-black">{formatMoney(total)}</p>
      </div>

      {/* CTA */}
      <button
        onClick={() => navigate("/checkout")}
        className="
          w-full h-12 rounded-full
          bg-brand-brown text-brand-ivory
          text-[10px] uppercase tracking-[0.55em]
          hover:opacity-95 transition
        "
      >
        {t("cart.pageProceed")}
      </button>

      <p className="text-[10px] uppercase tracking-[0.35em] text-black/35">
        {t("cart.pageFooterNote")}
      </p>
    </div>
  );
}
